import { useState, useEffect } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import { fetchSubscription } from '../../utils/fetchSubscription';
import { auth, db } from '../../firebase';
import { doc, setDoc, serverTimestamp, Timestamp, getDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore'; // + collection/getDocs/query/orderBy/limit

// Phone helpers
const sanitizePhoneNumber = (value = '') => value.toString().replace(/\D/g, '').slice(0, 10);
const normalizePhoneDigits = (value = '', countryCode = '+91') => {
  const digits = value?.toString().replace(/\D/g, '') || '';
  const codeDigits = (countryCode || '+91').replace('+', '');
  if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
    return sanitizePhoneNumber(digits.slice(codeDigits.length));
  }
  return sanitizePhoneNumber(digits.length > 10 ? digits.slice(-10) : digits);
};

const Plan = () => {
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' | 'yearly'
  const { collapsed, isMobile } = useSidebar();
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState('');
  const [currentBillingCycle, setCurrentBillingCycle] = useState(''); // tracks the user's active billing cycle
  const [loadingPlan, setLoadingPlan] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Billing modal state
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [billing, setBilling] = useState({
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    phoneCountryCode: '+91',
    address1: '',
    address2: '',
    city: '',
    state: '',
    code: '',
    country: ''
  });

  const PLANS = {
    Free: { id: null },
    Lite: {
      monthly: 'plan_RFdNRiSuMM6lmE',   
      yearly: 'plan_RFdSm32MLIVgzK'      
    },
    Plus: {
      monthly: 'plan_RFdNoWRV0EbGAX',   
      yearly: 'plan_RFdS1dga6zYNc2'      
    }
  };

  const CREATE_SUBSCRIPTION_API = process.env.REACT_APP_CREATE_SUBSCRIPTION_API;
  const CANCEL_SUBSCRIPTION_API = process.env.REACT_APP_CANCEL_SUBSCRIPTION_API;
  const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const hasBillingInfo = (p = {}) => {
    const req = [
      'firstName',
      'emailAddress',
      'phoneNumber',
      'address1',
      'state',
      'code',
      'country'
    ];
    return req.every((k) => (p[k] || '').toString().trim().length > 0);
  };

  const ensureBillingInfoOrPrompt = async (uid) => {
    try {
      const ref = doc(db, 'users', uid, 'UserProfile', uid);
      const snap = await getDoc(ref);
      const profile = snap.exists() ? (snap.data() || {}) : {};

      if (hasBillingInfo(profile)) {
        return { ok: true, profile };
      }

      const storedPhoneCode = profile.phoneCountryCode || '+91';
      const normalizedPhone = normalizePhoneDigits(profile.phoneNumber, storedPhoneCode);

      // Pre-fill modal with any known values
      setBilling({
        firstName: (profile.firstName || user?.firstName || user?.displayName || '').toString(),
        lastName: (profile.lastName || user?.lastName || '').toString(),
        emailAddress: (profile.emailAddress || user?.email || '').toString(),
        phoneNumber: normalizedPhone,
        phoneCountryCode: storedPhoneCode,
        address1: (profile.address1 || '').toString(),
        address2: (profile.address2 || '').toString(),
        city: (profile.city || '').toString(),
        state: (profile.state || '').toString(),
        code: (profile.code || profile.postalCode || profile.zip || '').toString(),
        country: (profile.country || 'India').toString()
      });
      setShowBillingModal(true);
      return { ok: false };
    } catch (e) {
      // If we cannot read profile, force prompt
      setShowBillingModal(true);
      return { ok: false };
    }
  };

  const saveBillingAndContinue = async () => {
    try {
      // Minimal validation
      const req = ['firstName', 'emailAddress', 'phoneNumber', 'address1', 'state', 'code', 'country'];
      const missing = req.filter((k) => !billing[k] || String(billing[k]).trim() === '');
      if (missing.length) {
        setErrorMessage('Please fill all required billing fields.');
        return;
      }
      const sanitized = sanitizePhoneNumber(billing.phoneNumber);
      if (sanitized.length !== 10) {
        setErrorMessage('Please enter a valid 10-digit phone number.');
        return;
      }

      const uid = auth?.currentUser?.uid || user?.uid;
      if (!uid) return;
      const ref = doc(db, 'users', uid, 'UserProfile', uid);

      await setDoc(ref, {
        ...billing,
        phoneNumber: sanitized,
        phoneCountryCode: billing.phoneCountryCode || '+91'
      }, { merge: true });
      setShowBillingModal(false);

      const planToResume = pendingPlan?.planKey;
      setPendingPlan(null);
      if (planToResume) {
        // Resume subscription flow without re-checking billing
        await handleSubscribe(planToResume, { skipBillingCheck: true });
      }
    } catch (e) {
      setErrorMessage('Failed to save billing info. Please try again.');
    }
  };

  // Track subscription doc id and renewal info for downgrade window
  // const [subscriptionDocId, setSubscriptionDocId] = useState(null); // removed: unused state
  const [renewsOnDate, setRenewsOnDate] = useState(null); // Date or null
  const [daysToRenewal, setDaysToRenewal] = useState(null); // number or null
  // Track lifecycle status for active-only enforcement
  const [currentLifecycle, setCurrentLifecycle] = useState(''); // created | active | renewed | paused | cancelled
  const [razorpayStatus, setRazorpayStatus] = useState(''); // pending | active | ...

  // Helper: safe to Date
  const toDate = (val) => {
    if (!val) return null;
    if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
    if (typeof val === 'number') return new Date(val * 1000);
    if (typeof val === 'string') {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  // Date formatting and downgrade window start
  const formatDate = (d) => {
    try {
      return new Intl.DateTimeFormat('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
    } catch {
      return d?.toISOString()?.slice(0, 10) || '';
    }
  };
  const getDowngradeEligibilityDate = () => {
    if (!renewsOnDate) return null;
    const d = new Date(renewsOnDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    return d;
  };
  const getDowngradeBlockedLabel = () => {
    const d = getDowngradeEligibilityDate();
    return `Not eligible for downgrade till ${d ? formatDate(d) : 'last 7 days before renewal'}`;
  };

  // Plan ordering to determine downgrades
  const PLAN_ORDER = { Free: 0, Lite: 1, Plus: 2 };
  const getPlanRank = (p) => (PLAN_ORDER[p] ?? 0);

  // True if selecting planKey with current UI billingCycle is a downgrade
  const isTargetDowngrade = (planKey) => {
    const curRank = getPlanRank(currentPlan);
    const targetRank = getPlanRank(planKey);
    if (targetRank < curRank) return true; // cheaper plan
    if (targetRank === curRank && currentPlan !== 'Free') {
      // same plan: yearly -> monthly is downgrade, monthly -> yearly is upgrade
      if (currentBillingCycle === 'yearly' && billingCycle === 'monthly') return true;
    }
    return false;
  };

  // Active-only enforcement (not pending/cancelled)
  const isLifecycleActive = () => {
    const s = (currentLifecycle || '').toLowerCase();
    const rz = (razorpayStatus || '').toLowerCase();
    if (rz === 'pending') return false;
    return s === 'active' || s === 'renewed' || s === 'created';
  };

  // Replace cross-cycle block with 7-day downgrade window logic (active-only)
  const isCrossCycleBlocked = (planKey) => {
    if (!isTargetDowngrade(planKey)) return false;
    if (!isLifecycleActive()) return false; // only enforce when active
    if (typeof daysToRenewal === 'number') {
      return daysToRenewal > 7;
    }
    return false; // unknown -> do not block
  };

  const handleSubscribe = async (planKey, opts = {}) => {
    if (!user?.uid) {
      setErrorMessage('You must be logged in to subscribe.');
      return;
    }
    // Downgrade enforcement: block until last 7 days (active-only)
    try {
      if (isTargetDowngrade(planKey) && isLifecycleActive()) {
        if (typeof daysToRenewal === 'number' && daysToRenewal > 7) {
          const d = getDowngradeEligibilityDate();
          setErrorMessage(`Not eligible for downgrade till ${d ? formatDate(d) : 'last 7 days before renewal'}.`);
          return;
        }
      }
    } catch (_) {
      // best-effort: if we cannot compute, proceed
    }

    setErrorMessage('');
    setLoadingPlan(planKey);
    try {
      // Obtain token
      let token = null;
      try {
        if (auth && auth.currentUser && typeof auth.currentUser.getIdToken === 'function') {
          token = await auth.currentUser.getIdToken(true);
        } else if (user && typeof user.getIdToken === 'function') {
          token = await user.getIdToken();
        } else if (user && user.accessToken) {
          token = user.accessToken;
        } else if (user && user.stsTokenManager && user.stsTokenManager.accessToken) {
          token = user.stsTokenManager.accessToken;
        }
      } catch (tkErr) {
        console.warn('Token retrieval failed', tkErr);
      }
      if (!token) {
        throw new Error('Failed to obtain Firebase ID token. Please sign in again.');
      }

      const planConfig = PLANS[planKey];
      const plan_id = planConfig?.[billingCycle] || planConfig?.id;

      if (!plan_id && planKey !== 'Free') {
        throw new Error('Plan not configured. Contact support.');
      }

      // NEW: Billing pre-check for paid plans
      if (planKey !== 'Free' && !opts.skipBillingCheck) {
        const ensured = await ensureBillingInfoOrPrompt(user.uid);
        if (!ensured.ok) {
          setPendingPlan({ planKey });
          setLoadingPlan(''); // stop spinner while user fills the modal
          return;
        }
      }

      if (planKey === 'Free') {
        try {
          const { subscription } = await fetchSubscription(user.uid);
          const subscriptionDocId = subscription?.id || null;
          const priorRazorpayId = subscription?.subscription_id || subscription?.subscriptionId || null;

          if (priorRazorpayId && priorRazorpayId !== 'Free') {
            try {
              const cancelEndpoint = CANCEL_SUBSCRIPTION_API;
              const cancelResp = await fetch(cancelEndpoint, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ subscription_id: priorRazorpayId })
              });
              const cancelJson = await cancelResp.json().catch(() => null);
              if (!cancelResp.ok) {
                console.warn('Cancel request returned non-ok', cancelResp.status, cancelJson);
              } else {
                console.log('Cancel request accepted', cancelJson);
              }
            } catch (cErr) {
              console.warn('Cancel endpoint call failed', cErr);
            }
          }
          const subscriptionDocToUpdate = subscriptionDocId || 'Free';
          const subRef = doc(db, 'users', user.uid, 'Subscription', subscriptionDocToUpdate);
          await setDoc(subRef, {
            subscription_id: 'Free',
            plan: 'Free',
            plan_id: null,
            billingCycle: '',
            status: 'active',
            updated_at: serverTimestamp()
          }, { merge: true });
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, {
            currentPlan: 'Free',
            billingCycle: '',
            subscription_id: subscriptionDocToUpdate,
            updated_at: serverTimestamp()
          }, { merge: true });
          setCurrentPlan('Free');
          setCurrentBillingCycle('');
          return;
        } catch (errFree) {
          console.error('Failed to switch to Free plan:', errFree);
          throw errFree;
        } finally {
          setLoadingPlan('');
        }
      }

      const total_count = billingCycle === 'monthly' ? 12 : 5;
      const payload = { plan_id, total_count, customer_notify: false };

      const funcsBase = CREATE_SUBSCRIPTION_API;
      const res = await fetch(funcsBase, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || data.details || 'Subscription creation failed');
      }

      const subscription = data.subscription || {};
      const subscription_id = subscription.id || subscription.subscription_id;
      if (!subscription_id) {
        throw new Error('No subscription id returned from server');
      }

const ok = await loadRazorpayScript();
if (!ok || !window.Razorpay) {
  throw new Error('Failed to load Razorpay checkout script');
}
const subFromServer = subscription;
const idTokenForCancel = token;

const options = {
  key: RAZORPAY_KEY,
  subscription_id: subscription_id,
  name: '1xSkills',
  description: `${planKey} plan`,
  prefill: {
    email: user?.email || user?.emailAddress || '',
    contact: user?.phoneNumber || '',
    name: user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || ''
  },
  handler: async function (response) {
    try {
      const paymentId = response?.razorpay_payment_id || null;
      const razorpaySubscriptionId = response?.razorpay_subscription_id || subscription_id;
      const uid = auth?.currentUser?.uid || user?.uid;
      if (!uid || !razorpaySubscriptionId) {
        return;
      }
      const maybeEpoch = subFromServer?.current_end || subFromServer?.current_period_end || subFromServer?.end_at || null;
      let renewsOn = null;
      if (typeof maybeEpoch === 'number' && !Number.isNaN(maybeEpoch)) {
        renewsOn = new Date(maybeEpoch * 1000).toISOString();
      } else if (typeof maybeEpoch === 'string' && maybeEpoch) {
        renewsOn = maybeEpoch;
      }

      let existingSubId = null;
      let priorRazorpayId = null;
      try {
        const fetched = await fetchSubscription(uid);
        existingSubId = fetched?.subscription?.id || fetched?.subscription?.subscription_id || null;
        priorRazorpayId = fetched?.subscription?.subscription_id || fetched?.subscription?.subscriptionId || null;
      } catch (fetchErr) {
      }
      const subscriptionDocId = existingSubId || 'Free';

      const planName = planKey ? (planKey.charAt(0).toUpperCase() + planKey.slice(1).toLowerCase()) : planKey;
      const subRef = doc(db, 'users', uid, 'Subscription', subscriptionDocId);
      await setDoc(subRef, {
        subscription_id: razorpaySubscriptionId,
        plan: planName,
        plan_id: plan_id || null,
        billingCycle,
        status: 'active',
        payment_id: paymentId,
        renews_on: renewsOn,
        updated_at: serverTimestamp()
      }, { merge: true });

      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, {
        currentPlan: planName,
        billingCycle,
        subscription_id: subscriptionDocId,
        updated_at: serverTimestamp()
      }, { merge: true });
      try {
        if (priorRazorpayId && priorRazorpayId !== razorpaySubscriptionId) {
          const cancelEndpoint = CANCEL_SUBSCRIPTION_API;
          const cancelResp = await fetch(cancelEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idTokenForCancel}`
            },
            body: JSON.stringify({ subscription_id: priorRazorpayId })
          });
          const cancelJson = await cancelResp.json().catch(() => null);
          if (!cancelResp.ok) {
            console.warn('Cancel request failed', cancelResp.status, cancelJson);
          } else {
          }
        }
      } catch (cancelErr) {
      }

      try {
        const PLAN_DEFAULTS = {
          Free: { chat: 50, fileUploadLimit: 50, flashcards: 10, notes: 10, quiz: 10, summary: 10, uploads: 15 },
          Lite: { chat: 1000, fileUploadLimit: 100, flashcards: 50, notes: 50, quiz: 50, summary: 50, uploads: 100 },
          Plus: { chat: 100000, fileUploadLimit: 300, flashcards: 500, notes: 500, quiz: 300, summary: 300, uploads: 200 },
        };
        const defaults = PLAN_DEFAULTS[planName] || PLAN_DEFAULTS.Free;
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const limitsRef = doc(db, 'users', uid, 'Subscription', subscriptionDocId, 'Limits', subscriptionDocId);
        await setDoc(limitsRef, {
          ...defaults,
          resetAt: Timestamp.fromDate(tomorrow),
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (limitsErr) {
        console.warn('Failed to update Limits after plan change:', limitsErr);
      }
      setCurrentPlan(planName);
      setCurrentBillingCycle(billingCycle);
    } catch (fsErr) {
      console.error('Failed to update Firestore after payment:', fsErr);
    }
  },
  modal: {
    ondismiss: function () {
      // user closed the checkout
      console.log('Checkout dismissed');
    }
  }
};
const rzp = new window.Razorpay(options);
rzp.open();
// Note: server saved subscription_id & status to Firestore. Optionally refresh subscription state.
setCurrentPlan(planKey);
setCurrentBillingCycle(billingCycle);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || 'Subscription failed');
    } finally {
      // If we opened the modal we already cleared loading; avoid flipping during modal
      if (!showBillingModal) setLoadingPlan('');
    }
  };
  
  useEffect(() => {
    const getPlan = async () => {
      if (user?.uid) {
        const { subscription } = await fetchSubscription(user.uid);
        const planFromSub = (subscription?.plan || 'Free');
        const cycleFromSub = subscription?.billingCycle || '';
        setCurrentPlan(planFromSub);
        setCurrentBillingCycle(cycleFromSub);

        // Track subscription doc id to read History
        const subDocId = subscription?.id || null;

        // Compute next renewal date and lifecycle status from latest History
        let renewAny = subscription?.renews_on || null; // may be ISO string
        let renewDate = toDate(renewAny);

        let lifecycle = (subscription?.status || '').toString();
        let rzStatus = '';

        // If missing, try latest History doc for this subscription
        if (user?.uid && subDocId) {
          try {
            const histRef = collection(db, 'users', user.uid, 'Subscription', subDocId, 'History');
            const q = query(histRef, orderBy('synced_at', 'desc'), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) {
              const h = snap.docs[0].data() || {};
              renewDate = renewDate || (toDate(h.renews_on) || toDate(h.renewed_at) || null);
              lifecycle = (h.status || lifecycle || '').toString();
              rzStatus = (h.razorpay_status || '').toString();
            }
          } catch (e) {
            // ignore, best-effort
          }
        }

        setRenewsOnDate(renewDate);
        setCurrentLifecycle(lifecycle);
        setRazorpayStatus(rzStatus);
        if (renewDate) {
          const now = new Date();
          const diffMs = renewDate.getTime() - now.getTime();
          const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          setDaysToRenewal(days > 0 ? days : 0);
        } else {
          setDaysToRenewal(null);
        }
      } else {
        setCurrentPlan('Free');
        setCurrentBillingCycle('');
        setRenewsOnDate(null);
        setDaysToRenewal(null);
        setCurrentLifecycle('');
        setRazorpayStatus('');
      }
    };
    getPlan();
  }, [user]);

  const prices = {
    Lite: billingCycle === 'yearly' ? 149 : 299,
    Plus: billingCycle === 'yearly' ? 249 : 499,
  };

  // Shift right if sidebar is expanded and not mobile
  const containerStyle = !collapsed && !isMobile
    ? { marginLeft: '16rem', transition: 'margin-left 0.3s' }
    : {};

  // Helpers to decide current/blocked states
  const isCurrentStrict = (planKey) => {
    return currentPlan === planKey && currentBillingCycle === billingCycle;
  };
  // Block downgrade (cheaper plan or yearly->monthly same plan) unless within last 7 days
  // Note: kept the same function name to minimize UI changes.
  // REMOVED DUPLICATE isCrossCycleBlocked FUNCTION

  // Lock scroll when billing modal is open
  useEffect(() => {
    if (!showBillingModal) return;
    const body = document.body;
    const originalOverflow = body.style.overflow;
    const originalPaddingRight = body.style.paddingRight;

    // Compensate for scrollbar to avoid layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = originalOverflow;
      body.style.paddingRight = originalPaddingRight;
    };
  }, [showBillingModal]);

  const COUNTRIES = [
    { value: 'India', label: '🇮🇳 India' },
    // ...add more countries later if needed
  ];
  const INDIA_STATES_UT = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu','Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'
  ];

  return (
    // Softer background with decorative blur
    <section
      className="py-16 mt-10 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden"
      style={containerStyle}
    >
      <div className="absolute inset-x-0 -top-20 h-40 bg-gradient-to-r from-blue-200/30 via-purple-200/30 to-pink-200/30 filter blur-3xl pointer-events-none" />
      <div className="container px-4 mx-auto">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-black">
            Retain More Knowledge
          </h1>
          <p className="mt-2 text-neutral-500">
            Upgrade for more uploads, longer files, faster models, and premium tools.
          </p>

          {/* Billing toggle - improved focus/hover */}
          <div className="inline-flex items-center mt-6 rounded-full border border-gray-200 p-1 bg-gray-100 shadow-sm">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm rounded-full transition-colors ${
                billingCycle === 'monthly' ? 'bg-black text-white shadow-sm' : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              Pay Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm rounded-full transition-colors flex items-center gap-2 ${
                billingCycle === 'yearly' ? 'bg-black text-white shadow-sm' : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              Pay Yearly
              <span className="text-xs text-white bg-blue-800 px-2 py-0.5 rounded-full">Save 40%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="relative grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-10">
          {/* Free */}
          <div className="border border-gray-2 00 rounded-2xl bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-black">Free</h3>
              <div className="mt-2">
                <div className="text-4xl font-semibold text-black">₹0</div>
                <div className="text-neutral-500 text-sm">/ month</div>
              </div>

              <div className="h-px bg-gray-200/70 mt-6" />
              <ul className="mt-6 space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>5 uploads/day</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>15 AI chats/day (1,00,000 tokens monthly)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>5 Quizzes/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>5 Summaries/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>5 Flashcards/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>5 Notes Generation/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>File limit: 25 MB</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>Standard Model Access</span>
                </li>
              </ul>

              {(() => {
                const strict = currentPlan === 'Free' && currentBillingCycle === '';
                const blocked = isTargetDowngrade('Free') && isCrossCycleBlocked('Free');
                if (blocked) {
                  return (
                    <div className="mt-6 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                      {getDowngradeBlockedLabel()}
                    </div>
                  );
                }
                return (
                  <button
                    className={`mt-6 w-full py-3 rounded-full transition-colors ${
                      strict ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'
                    }`}
                    disabled={strict || loadingPlan === 'Free'}
                    onClick={() => handleSubscribe('Free')}
                  >
                    {strict ? 'Current Plan' : 'Switch to Free'}
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Lite */}
          <div className="border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all relative">
            {loadingPlan === 'Lite' && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl bg-white/85 backdrop-blur-sm">
                <div className="relative mb-1">
                  <div className="absolute inset-0 w-14 h-14 rounded-full border-4 border-blue-200 animate-ping" />
                  <div className="w-14 h-14 rounded-full border-4 border-blue-500/70 border-t-transparent animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-blue-600 animate-pulse" />
                  </div>
                </div>
                <div className="flex gap-1 mt-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                </div>
              </div>
            )}
            <div className={`p-6 ${loadingPlan === 'Lite' ? 'opacity-60 pointer-events-none select-none' : ''}`}>
              <div className="text-sm text-neutral-500 mb-1">
                {billingCycle === 'yearly' ? 'Billed Annually' : 'Billed Monthly'}
              </div>
              <h3 className="text-xl font-semibold text-black">Lite</h3>
              <div className="mt-2">
                <div className="text-4xl font-semibold text-black">₹ {prices.Lite}</div>
                <div className="text-neutral-500 text-sm">/ month</div>
              </div>

              <div className="h-px bg-gray-200/70 mt-6" />
              <ul className="mt-6 space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>50 uploads/day</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>100 AI chats/day (30,00,000 tokens monthly)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>25 Quizzes/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>25 Summaries/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>25 Flashcards/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>25 Notes Generation/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>File limit: 100 MB</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <span>Gemini 2.5 Flash & GPT-5 Mini Access</span>
                </li>
              </ul>

              {(() => {
                const strict = isCurrentStrict('Lite');
                const blocked = isTargetDowngrade('Lite') && isCrossCycleBlocked('Lite');
                if (blocked) {
                  return (
                    <div className="mt-6 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                      {getDowngradeBlockedLabel()}
                    </div>
                  );
                }
                return (
                  <button
                    className={`mt-6 w-full py-3 rounded-full transition-colors flex items-center justify-center gap-2 ${
                      strict ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'
                    }`}
                    disabled={strict || loadingPlan === 'Lite'}
                    onClick={() => handleSubscribe('Lite')}
                  >
                    {loadingPlan === 'Lite' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {strict ? 'Current Plan' : (loadingPlan === 'Lite' ? 'Please wait...' : 'Upgrade to Lite')}
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Plus (Best Value) */}
          <div className="relative rounded-2xl p-px bg-gradient-to-r from-blue-800 to-blue-600 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
            {loadingPlan === 'Plus' && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center rounded-2xl bg-white/85 backdrop-blur-sm">
                <div className="relative mb-1">
                  <div className="absolute inset-0 w-14 h-14 rounded-full border-4 border-blue-200 animate-ping" />
                  <div className="w-14 h-14 rounded-full border-4 border-blue-500/70 border-t-transparent animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-blue-600 animate-pulse" />
                  </div>
                </div>
                <div className="flex gap-1 mt-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                </div>
              </div>
            )}
            <div className={`absolute p-2 -top-3 left-1/2 -translate-x-1/2 ${loadingPlan === 'Plus' ? 'z-40' : ''}`}> 
              <span className="px-3 py-1 text-xs font-semibold bg-white text-black rounded-md shadow-sm">Best Value</span>
            </div>
            <div className={`rounded-2xl bg-gray-900 text-white relative ${loadingPlan === 'Plus' ? 'opacity-60 pointer-events-none select-none' : ''}`}> 
              <div className="p-6">
                <div className="text-sm text-neutral-300 py-2 mb-1">
                  {billingCycle === 'yearly' ? 'Billed Annually' : 'Billed Monthly'}
                </div>
                <h3 className="text-xl font-semibold">Plus</h3>
                <div className="mt-2">
                  <div className="text-4xl font-semibold">₹ {prices.Plus}</div>
                  <div className="text-neutral-300 text-sm">/ month</div>
                </div>

                <div className="h-px bg-white/10 mt-6" />
                <ul className="mt-6 space-y-2 text-sm text-neutral-200">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Unlimited uploads/day</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Unlimited Chats/day (Unlimited tokens)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Unlimited Quizzes/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Unlimited Summaries/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Unlimited Notes Generation/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Unlimited Flashcards/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>File limit: 300 MB</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 mt-0.5" />
                    <span>Lite Models + Gemini 2.5 Pro, GPT-5 Access</span>
                  </li>
                </ul>

                {(() => {
                  const strict = isCurrentStrict('Plus');
                  const blocked = isTargetDowngrade('Plus') && isCrossCycleBlocked('Plus');
                  if (blocked) {
                    return (
                      <div className="mt-6 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm">
                        {getDowngradeBlockedLabel()}
                      </div>
                    );
                  }
                  return (
                    <button
                      className={`mt-6 w-full py-3 rounded-full transition-colors flex items-center justify-center gap-2 ${
                        strict ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-white text-black hover:bg-gray-100'
                      }`}
                      disabled={strict || loadingPlan === 'Plus'}
                      onClick={() => handleSubscribe('Plus')}
                    >
                      {loadingPlan === 'Plus' && <Loader2 className="w-4 h-4 animate-spin" />}
                      {strict ? 'Current Plan' : (loadingPlan === 'Plus' ? 'Please wait...' : 'Upgrade to Plus')}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center text-sm text-neutral-500 mt-8">
          14-day money-back guarantee. Need an enterprise plan?{' '}
          <a href="mailto:support@1xskills.com" className="underline hover:text-neutral-700">
            Contact sales
          </a>
          .
        </div>

        {errorMessage && (
          <div className="mt-4 text-center text-sm text-red-600">{errorMessage}</div>
        )}
      </div>

      {/* Billing Modal */}
      {showBillingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-black">Billing information</h2>
            <p className="text-sm text-neutral-600 mt-1">Please complete your billing details to continue.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {/* ...existing name inputs... */}
              <input
                className="border rounded-md p-2 md:col-span-2"
                placeholder="Email *"
                value={billing.emailAddress}
                onChange={(e) => setBilling({ ...billing, emailAddress: e.target.value })}
              />

              {/* Phone: India code dropdown + 10-digit input */}
              <div className="md:col-span-2 flex gap-2">
                <select
                  className="border rounded-md p-2 w-28"
                  value={billing.phoneCountryCode || '+91'}
                  onChange={(e) => setBilling({ ...billing, phoneCountryCode: e.target.value })}
                >
                  <option value="+91">🇮🇳 +91</option>
                </select>
                <input
                  className="border rounded-md p-2 flex-1"
                  placeholder="Enter 10-digit phone number *"
                  value={billing.phoneNumber}
                  maxLength={10}
                  onChange={(e) => setBilling({ ...billing, phoneNumber: sanitizePhoneNumber(e.target.value) })}
                />
              </div>

              {/* ...existing address lines... */}
              <input
                className="border rounded-md p-2 md:col-span-2"
                placeholder="Address line 2"
                value={billing.address2}
                onChange={(e) => setBilling({ ...billing, address2: e.target.value })}
              />
              <input
                className="border rounded-md p-2"
                placeholder="City"
                value={billing.city}
                onChange={(e) => setBilling({ ...billing, city: e.target.value })}
              />

              {/* State / UT for India */}
              {(billing.country || 'India') === 'India' ? (
                <select
                  className="border rounded-md p-2"
                  value={billing.state || ''}
                  onChange={(e) => setBilling({ ...billing, state: e.target.value })}
                >
                  <option value="" disabled>Select state/UT</option>
                  {INDIA_STATES_UT.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="border rounded-md p-2"
                  placeholder="State *"
                  value={billing.state}
                  onChange={(e) => setBilling({ ...billing, state: e.target.value })}
                />
              )}

              <input
                className="border rounded-md p-2"
                placeholder="Postal code *"
                value={billing.code}
                onChange={(e) => setBilling({ ...billing, code: e.target.value })}
              />

              {/* Country dropdown, default India */}
              <select
                className="border rounded-md p-2"
                value={billing.country || 'India'}
                onChange={(e) => setBilling({ ...billing, country: e.target.value, state: '' })}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button className="px-4 py-2 rounded-md border text-neutral-700"
                onClick={() => { setShowBillingModal(false); setPendingPlan(null); }}>
                Cancel
              </button>
              <button className="px-4 py-2 rounded-md bg-black text-white"
                onClick={saveBillingAndContinue}>
                Save & Continue
              </button>
            </div>

            {errorMessage && <div className="mt-3 text-sm text-red-600">{errorMessage}</div>}
          </div>
        </div>
      )}
    </section>
  );
};

export default Plan;
